"use client";
import dynamic from "next/dynamic";
import React from "react";
const CalendarComponent = dynamic(() => import("./components/calendar"), {
    ssr: false,
    loading: () => <CalendarMockup />,
});
import { usePathname } from "next/navigation";
import { iconpath } from "@/util/staticresourcespath";
import { languages } from "@/app/i18n/settings";
type Props = {
    loc: string;
    res: any;
};
import { useMouseMovement } from "@/app/[lang]/MouseMovementContext";
import { Language } from "@/app/i18n/settings";
import ColorStyleContext from "@/lib/providers/color-style-privider";
import { useContext } from "react";

const CalendarMockup = () => {
    const pathName = usePathname();
    const loc = languages.find((l) => pathName.startsWith(`/${l}`)) ?? "th";
    const calendar = generateMonthCalendar({ loc });
    const currentDate = new Date();
    const currentDay = currentDate.getDate();
    const monthNamesTH = [
        "มกราคม",
        "กุมภาพันธ์",
        "มีนาคม",
        "เมษายน",
        "พฤษภาคม",
        "มิถุนายน",
        "กรกฎาคม",
        "สิงหาคม",
        "กันยายน",
        "ตุลาคม",
        "พฤศจิกายน",
        "ธันวาคม",
    ];

    const monthNamesEN = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
    ];

    const currentMonthIndex = new Date().getMonth();
    const currentMonth = loc === "th" ? monthNamesTH[currentMonthIndex] : monthNamesEN[currentMonthIndex];
    let currentYear = new Date().getFullYear();
    if (loc === "th") {
        currentYear += 543;
    }
    const colorStyle = useContext(ColorStyleContext);
    return (
        <>
            <div className="flex flex-row items-center gap-4">
                <div className="relative">
                    <img
                        src={iconpath + "vector_right.svg"}
                        alt={"vector_right"}
                        width={11}
                        height={0}
                        style={{ width: "100%", height: "auto" }}
                        className="cursor-pointer rotate-180"
                    />
                </div>

                <span className="text-2xl font-bold whitespace-nowrap"
                    style={{ color: colorStyle?.headingMain || "#006ef2" }}>
                    {currentMonth} {currentYear}
                </span>
                <div className="relative">
                    <img
                        src={iconpath + "vector_right.svg"}
                        alt={"vector_right"}
                        width={11}
                        height={0}
                        style={{ width: "100%", height: "auto" }}
                        className="cursor-pointer"
                    />
                </div>
            </div>
            <div className="mx-auto bg-gray-100 rounded-lg border">
                <table className="table-auto">
                    <thead>
                        <tr>
                            {(loc === "th"
                                ? ["จ.", "อ.", "พ.", "พฤ.", "ศ.", "ส.", "อา."]
                                : ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
                            ).map((day) => (
                                <th
                                    className="border-r-[1px] w-[191px] h-14 pl-4 text-left bg-white rounded-t-lg font-light"
                                    style={{ color: "#757575"}}
                                    key={day}>
                                    {day}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {calendar.map((week, weekIndex) => (
                            <tr key={weekIndex}>
                                {week.map((day, dayIndex) => (
                                    <td
                                        key={`${weekIndex}-${dayIndex}`}
                                        className={`h-[87.5px] sm:h-[157px] pl-4 pt-4 border align-text-top font-normal text-xl 
                                    `}
                                        style={{
                                            color: "#757575",
                                            backgroundColor:
                                                currentDay == day ? "#f3f4f6 !important" : day ? "#FFFFFF" : "#f3f4f6",
                                        }}>
                                        {day !== null ? day : ""}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
};

const EventCalendarComponentClient = (props: Props) => {
    const { hasMouseMoved } = useMouseMovement();
    const { loc, res } = props;
    return hasMouseMoved ? <CalendarComponent loc={loc} calendarComponent={res} /> : <CalendarMockup />;
};

export default EventCalendarComponentClient;

const generateMonthCalendar = ({ loc = "en" }: { loc: Language }) => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    // กำหนดให้วันที่เป็น 1 เพื่อหาวันแรกของเดือน
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
    const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);

    // ดึงข้อมูลวันที่เริ่มและสิ้นสุดของเดือน
    let startDayOfWeek = firstDayOfMonth.getDay(); // วันแรกของเดือน (0-6)
    const daysInMonth = lastDayOfMonth.getDate(); // จำนวนวันในเดือน

    // ถ้า loc เป็น "th" จะถือว่าเริ่มด้วยวันจันทร์
    if (loc === "th") {
        startDayOfWeek = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1; // ปรับให้ 0 (อาทิตย์) เป็น 6 และเลื่อนวันอื่น ๆ
    }

    // Array สำหรับเก็บวันที่ของเดือนในรูปแบบสัปดาห์
    const weeks = [];
    let week = new Array(7).fill(null); // สร้าง array เปล่าที่มี 7 ค่าเป็น null เพื่อแทนสัปดาห์แรก

    // เติมวันที่ใน array โดยไล่จาก 1 ถึงวันสิ้นสุดของเดือน
    for (let day = 1; day <= daysInMonth; day++) {
        const dayOfWeek = (startDayOfWeek + day - 1) % 7; // หาวันในสัปดาห์ของแต่ละวันที่
        week[dayOfWeek] = day;

        // เมื่อครบสัปดาห์ (7 วัน) หรือถึงวันสุดท้ายของเดือน ให้เริ่มสัปดาห์ใหม่
        if (dayOfWeek === 6 || day === daysInMonth) {
            weeks.push(week);
            week = new Array(7).fill(null); // เริ่ม array สัปดาห์ใหม่ด้วย null
        }
    }

    return weeks;
};
